import { Price } from '@vs-shop/schemas/primitive/price';
import type {
  ContinuousRangeValue,
  PriceRangeValue,
  RangeFilterValue,
} from '@vs-shop/schemas/product/filter/state/range';
import { bigDecimalToNumberSafe } from '@vs-shop/domain/price/bigDecimal';
import { priceToSlider, sliderToPrice } from '@vs-shop/domain/price/priceScale';
import { BigDecimal, Chunk, Match, Option, pipe } from 'effect';

import { FilterRangeNormalizedConfig } from '../../../config/filterSlider';

/**
 * Generic strategy interface for range filter value transformations
 *
 * Type parameter T ensures type safety:
 * - Price for price filters (quadratic scale, branded type)
 * - BigDecimal for continuous numeric filters (linear scale)
 *
 * Accepts BigDecimal.BigDecimal as input for flexibility, internal implementation
 * handles the specific type (Price is a branded BigDecimal).
 */
export type RangeFilterStrategy<T extends BigDecimal.BigDecimal = BigDecimal.BigDecimal> = {
  /**
   * Convert internal value to slider position (0-100 or actual range)
   * Accepts BigDecimal for flexibility since Price is a branded BigDecimal
   */
  valueToSlider(value: BigDecimal.BigDecimal): number;

  /**
   * Convert slider position to internal value
   */
  sliderToValue(position: number): T;

  /**
   * Get slider configuration (min, max, step for the slider component)
   */
  getSliderConfig(): { min: number; max: number; step: number };
};

/**
 * Price range filter strategy using quadratic (power of 2) scale
 *
 * Provides better UX for price ranges with fine-grained control at lower values
 * while still allowing selection of higher values. Uses normalized 0-100 slider
 * with quadratic transformation to actual price values.
 *
 * Generic type parameter ensures type-safe Price handling without casts.
 */
class PriceRangeFilterStrategy implements RangeFilterStrategy<Price> {
  constructor(
    private readonly min: Price,
    private readonly max: Price
  ) {}

  valueToSlider(value: BigDecimal.BigDecimal): number {
    // Price is a branded BigDecimal, so we can safely treat any BigDecimal as Price here
    return priceToSlider(Price(value), this.min, this.max);
  }

  sliderToValue(position: number): Price {
    return Price(sliderToPrice(position, this.min, this.max));
  }

  getSliderConfig() {
    return {
      min: FilterRangeNormalizedConfig.MIN,
      max: FilterRangeNormalizedConfig.MAX,
      step: FilterRangeNormalizedConfig.STEP,
    };
  }
}

/**
 * Continuous linear range filter strategy
 *
 * Direct linear mapping between slider position and actual values.
 * Used for numeric attributes like RAM, SSD capacity, warranty period, etc.
 *
 * Generic type parameter ensures type-safe BigDecimal handling without casts.
 */
class LinearRangeFilterStrategy implements RangeFilterStrategy {
  constructor(
    private readonly min: BigDecimal.BigDecimal,
    private readonly max: BigDecimal.BigDecimal,
    private readonly step: BigDecimal.BigDecimal
  ) {}

  valueToSlider(value: BigDecimal.BigDecimal): number {
    const numberOption = bigDecimalToNumberSafe(value);
    return Option.getOrElse(numberOption, () => 0);
  }

  sliderToValue(position: number): BigDecimal.BigDecimal {
    const bigDecimalOption = BigDecimal.fromNumber(position);
    return Option.getOrElse(bigDecimalOption, () => this.min);
  }

  getSliderConfig() {
    const minOption = bigDecimalToNumberSafe(this.min);
    const maxOption = bigDecimalToNumberSafe(this.max);
    const stepOption = bigDecimalToNumberSafe(this.step);

    return {
      min: Option.getOrElse(minOption, () => 0),
      max: Option.getOrElse(maxOption, () => 100),
      step: Option.getOrElse(stepOption, () => 1),
    };
  }
}

/**
 * Stepped range filter strategy with discrete value snapping
 *
 * Snaps slider to predefined discrete values instead of continuous range.
 * Used for attributes with specific tiers:
 * - RAM: [4GB, 8GB, 16GB, 32GB, 64GB]
 * - Storage: [128GB, 256GB, 512GB, 1TB, 2TB]
 * - Core counts: [2, 4, 6, 8, 12, 16]
 *
 * The slider uses indices (0, 1, 2, ...) for positions,
 * and maps them to actual values from the steps array.
 */
class SteppedRangeFilterStrategy implements RangeFilterStrategy {
  constructor(
    private readonly min: BigDecimal.BigDecimal,
    private readonly max: BigDecimal.BigDecimal,
    private readonly steps: Chunk.Chunk<BigDecimal.BigDecimal>
  ) {
    // Safety check: Must have at least 2 values to form a valid range
    if (Chunk.size(steps) < 2) {
      throw new Error(
        `[SteppedRangeFilterStrategy] Invalid steps array: must have at least 2 values, got ${String(Chunk.size(steps))}`
      );
    }
  }

  /**
   * Convert value to slider index position
   * Finds the closest step index for the given value using BigDecimal operations
   */
  valueToSlider(value: BigDecimal.BigDecimal): number {
    // Safety check: return 0 if no steps (shouldn't happen due to constructor validation)
    if (Chunk.isEmpty(this.steps)) {
      return 0;
    }

    // Normalize value for comparison
    const normalizedValue = BigDecimal.normalize(value);

    // Start with first step as initial minimum distance (can't use Infinity with BigDecimal)
    const firstStepOption = Chunk.head(this.steps);
    if (Option.isNone(firstStepOption)) {
      return 0;
    }

    const firstStep = BigDecimal.normalize(firstStepOption.value);
    const initialDistance = BigDecimal.abs(BigDecimal.subtract(firstStep, normalizedValue));

    // Find the index of the closest step using functional approach with BigDecimal operations
    const result = pipe(
      this.steps,
      Chunk.reduce(
        {
          index: 0,
          currentIdx: 0,
          minDistance: initialDistance,
        },
        (acc, step) => {
          const normalizedStep = BigDecimal.normalize(step);
          const distance = BigDecimal.abs(BigDecimal.subtract(normalizedStep, normalizedValue));

          // Use BigDecimal.isLessThan for comparison
          const newAcc = BigDecimal.isLessThan(distance, acc.minDistance)
            ? { index: acc.currentIdx, currentIdx: acc.currentIdx + 1, minDistance: distance }
            : { ...acc, currentIdx: acc.currentIdx + 1 };

          return newAcc;
        }
      )
    );

    return result.index;
  }

  /**
   * Convert slider index position to actual value
   * Returns the step at the given index using Chunk utilities
   */
  sliderToValue(position: number): BigDecimal.BigDecimal {
    const index = Math.round(position); // Ensure integer index
    const stepOption = Chunk.get(this.steps, index);

    return Option.match(stepOption, {
      onNone: () => (index <= 0 ? this.min : this.max),
      onSome: (step) => step,
    });
  }

  /**
   * Get slider configuration
   * Min=0, Max=steps.length-1, Step=1 (for index-based navigation)
   */
  getSliderConfig() {
    const maxIndex = Math.max(0, Chunk.size(this.steps) - 1);

    return {
      min: 0,
      max: maxIndex,
      step: 1, // Always step by 1 index
    };
  }
}

/**
 * Type aliases for specific strategy types
 */
export type PriceFilterStrategy = RangeFilterStrategy<Price>;
export type ContinuousFilterStrategy = RangeFilterStrategy;

/**
 * Factory function to create appropriate strategy based on range value type
 *
 * Uses exhaustive pattern matching on RangeFilterValue._tag:
 * - 'PriceRange': PriceRangeFilterStrategy (quadratic scale, branded Price type)
 * - 'ContinuousRange': LinearRangeFilterStrategy (linear scale, BigDecimal)
 *
 * @param rangeValue - Tagged union of range filter values
 * @param step - Step increment for continuous ranges (ignored for price ranges)
 * @returns Type-safe strategy instance (PriceFilterStrategy | ContinuousFilterStrategy)
 */
export function createRangeFilterStrategy(
  rangeValue: PriceRangeValue,
  step: BigDecimal.BigDecimal
): PriceFilterStrategy;

export function createRangeFilterStrategy(
  rangeValue: ContinuousRangeValue,
  step: BigDecimal.BigDecimal
): ContinuousFilterStrategy;

export function createRangeFilterStrategy(
  rangeValue: RangeFilterValue,
  step: BigDecimal.BigDecimal
): PriceFilterStrategy | ContinuousFilterStrategy;

export function createRangeFilterStrategy(
  rangeValue: RangeFilterValue,
  step: BigDecimal.BigDecimal
): PriceFilterStrategy | ContinuousFilterStrategy {
  return Match.value(rangeValue).pipe(
    Match.tag(
      'PriceRange',
      (priceRange) => new PriceRangeFilterStrategy(Price(priceRange.min), Price(priceRange.max))
    ),
    Match.tag(
      'ContinuousRange',
      (continuousRange) =>
        new LinearRangeFilterStrategy(continuousRange.min, continuousRange.max, step)
    ),
    Match.tag(
      'SteppedRange',
      (steppedRange) =>
        new SteppedRangeFilterStrategy(steppedRange.min, steppedRange.max, steppedRange.steps)
    ),
    Match.exhaustive
  );
}
